import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, DollarSign, Percent, Clock, Calculator } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CostingSetup = () => {
  const [settings, setSettings] = useState({
    labour_rate_per_hour: 85,
    design_rate_per_hour: 75,
    burden_rate_labour: 53,
    burden_rate_design: 53,
    material_surcharge_rate: 15,
    labour_based_rate: 10,
    consumable_supply_rate: 0,
    disposal_fee: 0,
    rush_job_multiplier: 1.5,
    gst_rate: 5,
    total_monthly_hours: 160,
    hours_buffer: 10
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [utilization, setUtilization] = useState({
    billable_hours: 0,
    available_hours: 160,
    utilization: 0,
    tier: 'standard'
  });

  useEffect(() => {
    loadSettings();
    loadUtilization();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/costing`);
      if (response.data) {
        setSettings(prev => ({...prev, ...response.data}));
      }
    } catch (error) {
      console.error('Failed to load costing settings:', error);
      toast.error('Failed to load costing settings');
    } finally {
      setLoading(false);
    }
  };

  const loadUtilization = async () => {
    try {
      const response = await axios.get(`${API}/costing/utilization`);
      if (response.data) {
        setUtilization(response.data);
      }
    } catch (error) {
      console.error('Failed to load utilization:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/costing`, settings);
      toast.success('Costing settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  // Calculate effective rates
  const effectiveLabourRate = settings.labour_rate_per_hour * (1 + settings.burden_rate_labour / 100);
  const effectiveDesignRate = settings.design_rate_per_hour * (1 + settings.burden_rate_design / 100);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-primary text-xl font-mono uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="costing-setup-page">
      <div className="mb-8">
        <h1 className="text-3xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2">
          Costing Setup
        </h1>
        <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
          Configure pricing and cost parameters
        </p>
      </div>

      {/* Utilization Dashboard */}
      <Card className="border-border mb-6">
        <CardHeader>
          <CardTitle className="text-lg font-bold uppercase tracking-tight flex items-center gap-2">
            <Clock size={20} />
            Shop Utilization
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-muted/30 rounded-sm">
              <p className="text-xs font-mono uppercase text-muted-foreground mb-1">Billable Hours</p>
              <p className="text-2xl font-bold font-mono">{utilization.billable_hours?.toFixed(0) || 0}h</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-sm">
              <p className="text-xs font-mono uppercase text-muted-foreground mb-1">Available</p>
              <p className="text-2xl font-bold font-mono">{utilization.available_hours?.toFixed(0) || 160}h</p>
            </div>
            <div className="p-4 bg-primary/10 rounded-sm">
              <p className="text-xs font-mono uppercase text-muted-foreground mb-1">Utilization</p>
              <p className="text-2xl font-bold font-mono text-primary">{utilization.utilization?.toFixed(1) || 0}%</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-sm">
              <p className="text-xs font-mono uppercase text-muted-foreground mb-1">Rate Tier</p>
              <p className="text-2xl font-bold font-mono capitalize">{utilization.tier || 'Standard'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Base Rates */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-lg font-bold uppercase tracking-tight flex items-center gap-2">
              <DollarSign size={20} />
              Base Rates
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs font-mono uppercase">Labour Rate ($/hr)</Label>
              <Input
                type="number"
                value={settings.labour_rate_per_hour}
                onChange={(e) => updateSetting('labour_rate_per_hour', e.target.value)}
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Effective: ${effectiveLabourRate.toFixed(2)}/hr (with burden)
              </p>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Design Rate ($/hr)</Label>
              <Input
                type="number"
                value={settings.design_rate_per_hour}
                onChange={(e) => updateSetting('design_rate_per_hour', e.target.value)}
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Effective: ${effectiveDesignRate.toFixed(2)}/hr (with burden)
              </p>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Rush Job Multiplier</Label>
              <Input
                type="number"
                step="0.1"
                value={settings.rush_job_multiplier}
                onChange={(e) => updateSetting('rush_job_multiplier', e.target.value)}
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Rush rate: ${(settings.labour_rate_per_hour * settings.rush_job_multiplier).toFixed(2)}/hr
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Burden & Overhead */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-lg font-bold uppercase tracking-tight flex items-center gap-2">
              <Percent size={20} />
              Burden & Overhead
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs font-mono uppercase">Labour Burden Rate (%)</Label>
              <Input
                type="number"
                value={settings.burden_rate_labour}
                onChange={(e) => updateSetting('burden_rate_labour', e.target.value)}
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Design Burden Rate (%)</Label>
              <Input
                type="number"
                value={settings.burden_rate_design}
                onChange={(e) => updateSetting('burden_rate_design', e.target.value)}
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Material Surcharge (%)</Label>
              <Input
                type="number"
                value={settings.material_surcharge_rate}
                onChange={(e) => updateSetting('material_surcharge_rate', e.target.value)}
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Labour-Based Rate (%)</Label>
              <Input
                type="number"
                value={settings.labour_based_rate}
                onChange={(e) => updateSetting('labour_based_rate', e.target.value)}
                className="mt-1 font-mono"
              />
            </div>
          </CardContent>
        </Card>

        {/* Additional Fees */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-lg font-bold uppercase tracking-tight flex items-center gap-2">
              <Calculator size={20} />
              Additional Fees
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs font-mono uppercase">Consumable Supply Rate (%)</Label>
              <Input
                type="number"
                value={settings.consumable_supply_rate}
                onChange={(e) => updateSetting('consumable_supply_rate', e.target.value)}
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Disposal Fee ($)</Label>
              <Input
                type="number"
                value={settings.disposal_fee}
                onChange={(e) => updateSetting('disposal_fee', e.target.value)}
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">GST Rate (%)</Label>
              <Input
                type="number"
                value={settings.gst_rate}
                onChange={(e) => updateSetting('gst_rate', e.target.value)}
                className="mt-1 font-mono"
              />
            </div>
          </CardContent>
        </Card>

        {/* Capacity Settings */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-lg font-bold uppercase tracking-tight flex items-center gap-2">
              <Clock size={20} />
              Capacity Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs font-mono uppercase">Total Monthly Hours</Label>
              <Input
                type="number"
                value={settings.total_monthly_hours}
                onChange={(e) => updateSetting('total_monthly_hours', e.target.value)}
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Available capacity per month
              </p>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Hours Buffer (%)</Label>
              <Input
                type="number"
                value={settings.hours_buffer}
                onChange={(e) => updateSetting('hours_buffer', e.target.value)}
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Safety buffer for scheduling
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="font-mono uppercase tracking-wider"
          data-testid="save-costing-btn"
        >
          <Save size={16} className="mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default CostingSetup;
